import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../../context/ConfigurationContext';
import { getLayoutOptions, getRecommendedLayoutId, isVisualProfileAvailable, getLayoutImage } from '../../constants/layouts';
import type { LayoutConfig } from '../../constants/layouts';
import { LayoutNo911Dialog } from '../../components/features/LayoutNo911Dialog';

interface LayoutSelectionProps {
  onNext: () => void;
  onBack: () => void;
}

const LayoutSelection: FC<LayoutSelectionProps> = ({ onNext, onBack }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  const [talkModalOpen, setTalkModalOpen] = useState(false);
  // D19 — the no-911 layout awaiting confirmation. Held separately from
  // `selectedLayoutId` so that backing out leaves the previous selection intact
  // rather than committing the risky one and undoing it afterwards.
  //
  // `advance` records where the dialog was raised from: picking a card (false)
  // or pressing Continue (true). Confirming from Continue has to both commit and
  // move on, or the caregiver would have to press Continue twice.
  const [pendingNo911, setPendingNo911] = useState<{ layout: LayoutConfig; advance: boolean } | null>(null);
  // Which no-911 layout the caregiver has already accepted, so the warning is
  // shown once per layout rather than again on Continue.
  const [acknowledgedNo911, setAcknowledgedNo911] = useState<string | null>(null);

  const {
    selectedProfile,
    severityLevel,
    selectedLayoutId,
    setSelectedLayout,
  } = useConfiguration();

  // Get layout options for the selected profile + severity (2-3 options)
  const layoutOptions = useMemo(() => {
    if (!selectedProfile) return [];
    return getLayoutOptions(selectedProfile, severityLevel);
  }, [selectedProfile, severityLevel]);

  // Get the recommended layout ID
  const recommendedLayoutId = useMemo(() => {
    if (!selectedProfile) return null;
    return getRecommendedLayoutId(selectedProfile, severityLevel);
  }, [selectedProfile, severityLevel]);

  // Auto-select recommended layout on mount
  useEffect(() => {
    if (recommendedLayoutId && !selectedLayoutId) {
      setSelectedLayout(recommendedLayoutId);
    }
  }, [recommendedLayoutId, selectedLayoutId, setSelectedLayout]);

  const handleLayoutSelect = (layout: LayoutConfig) => {
    // D19 gate. `has911` is the predicate rather than a hardcoded id list: it is
    // the same field the "911 Emergency" tag on the card reads, so a layout
    // added later is covered without anyone remembering to update a list.
    if (!layout.has911 && acknowledgedNo911 !== layout.id) {
      setPendingNo911({ layout, advance: false });
      return;
    }
    setSelectedLayout(layout.id);
  };

  const handleContinue = () => {
    if (!selectedLayoutId) return;
    // Also gate the exit, not just the click. The recommended layout is
    // auto-selected on mount (see the effect above), and for a severe cognitive
    // or visual assessment that recommendation IS a no-911 layout — so a
    // caregiver can reach this button having never clicked a card. Gating only
    // the click would let exactly the person this warning is for walk past it.
    const selected = layoutOptions.find(o => o.layout.id === selectedLayoutId)?.layout;
    if (selected && !selected.has911 && acknowledgedNo911 !== selected.id) {
      setPendingNo911({ layout: selected, advance: true });
      return;
    }
    onNext();
  };

  const confirmNo911 = () => {
    if (!pendingNo911) return;
    const { layout, advance } = pendingNo911;
    setAcknowledgedNo911(layout.id);
    setSelectedLayout(layout.id);
    setPendingNo911(null);
    if (advance) onNext();
  };

  // Profile display name
  const profileDisplayName = selectedProfile === 'cognitive'
    ? (isFr ? 'Mémoire et Confusion' : 'Memory & Confusion')
    : selectedProfile === 'physical'
    ? (isFr ? 'Physique' : 'Physical')
    : (isFr ? 'Visuel' : 'Visual');

  // Check if visual profile (coming soon)
  const isVisualComingSoon = selectedProfile === 'visual' && !isVisualProfileAvailable();

  return (
    <div className="min-h-screen bg-brand-background dark:bg-slate-900 pb-24">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-brand-border dark:border-slate-700 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-brand-text-light hover:text-brand-text transition-colors"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              {isFr ? 'Retour' : 'Back'}
            </button>
            <h2 className="font-display font-semibold text-brand-text dark:text-white">
              {isFr ? 'Choisir le modèle' : 'Choose Layout'}
            </h2>
            <span className="text-sm text-brand-text-muted">{isFr ? 'Étape 3 de 7' : 'Step 3 of 7'}</span>
          </div>

          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-brand-bg-warm dark:bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-brand-primary rounded-full transition-all duration-500" style={{ width: '43%' }} />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-brand-text dark:text-white mb-4 tracking-tight">
            {isFr ? "Choisissez le modèle de l'écran d'accueil" : 'Choose the home screen layout'}
          </h1>
          <p className="text-lg text-brand-text-light dark:text-slate-400 max-w-2xl mx-auto">
            {isFr
              ? `Selon votre évaluation ${profileDisplayName}, nous recommandons le modèle indiqué ci-dessous. Vous pouvez choisir une autre option si vous préférez.`
              : `Based on your ${profileDisplayName} assessment, we recommend the layout marked below. You can choose a different option if preferred.`}
          </p>
          {severityLevel && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-primary/10 rounded-full">
              <span className="material-symbols-outlined text-brand-primary text-sm">verified</span>
              <span className="text-sm font-medium text-brand-primary">
                {isFr
                  ? `Niveau ${severityLevel === 'severe' ? 'sévère' : severityLevel === 'moderate' ? 'modéré' : 'léger'}`
                  : `${severityLevel.charAt(0).toUpperCase() + severityLevel.slice(1)} support level`}
              </span>
            </div>
          )}
        </div>

        {/* Visual Profile - Coming Soon */}
        {isVisualComingSoon ? (
          <div className="max-w-md mx-auto text-center py-16">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-brand-primary/10 flex items-center justify-center">
              <span className="material-symbols-outlined text-4xl text-brand-primary">visibility</span>
            </div>
            <h2 className="text-2xl font-display font-bold text-brand-text dark:text-white mb-4">
              Visual Layouts Coming Soon
            </h2>
            <p className="text-brand-text-light dark:text-slate-400 mb-8">
              We're working on specialized layouts for visual impairments with enhanced voice controls
              and high-contrast interfaces. Check back soon!
            </p>
            <button
              onClick={onBack}
              className="px-6 py-3 bg-brand-primary text-white font-medium rounded-xl hover:bg-brand-primary-dark transition-colors"
            >
              Go Back
            </button>
          </div>
        ) : (
          <>
            {/* Layout Cards Grid - Good-Better-Best */}
            <div className={`grid gap-4 sm:gap-6 lg:gap-8 ${
              layoutOptions.length === 2
                ? 'md:grid-cols-2 max-w-3xl mx-auto'
                : 'md:grid-cols-2 lg:grid-cols-3'
            }`}>
              {layoutOptions.map((option) => (
                <LayoutCard
                  key={option.layout.id}
                  layout={option.layout}
                  isSelected={selectedLayoutId === option.layout.id}
                  isRecommended={option.isRecommended}
                  onSelect={() => handleLayoutSelect(option.layout)}
                />
              ))}
            </div>

            {/* TALK Info */}
            {/* D19 \u2014 a help line used to sit here claiming that every layout
                carried 911 emergency access and voice commands, in both
                languages. Both halves were FALSE: cb_1, vi_1 and vi_2 have
                has911: false, and cb_1 also has hasAssistance: false. So this
                screen promised a 911 button on the very layouts D19 exists to
                warn about \u2014 three cards up from an "Urgence 911" tag that
                correctly does NOT render on them. The exact wording is in the
                diff for this commit; it is deliberately not reproduced here.

                DELETED rather than reworded. It is copy for a capability those
                layouts do not have \u2014 the PR #43 fall-detection-FAQ precedent \u2014
                so no French had to be invented to replace it. The per-card
                "911 Emergency" / "Urgence 911" tags already tell the truth
                layout by layout, and the D19 dialog now says it out loud.

                `scripts/locked-facts.json` gained a `layout-911-equivalence`
                entry in the same commit so the claim cannot come back quietly.
                Nothing in the existing gate caught it: it names no banned
                feature, it simply was not true. */}
            <div className="mt-12 text-center">
              <button
                onClick={() => setTalkModalOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-primary hover:underline bg-transparent border-none cursor-pointer"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '18px' }}>mic</span>
                {isFr ? 'Qu\u2019est-ce que PARLER?' : 'What is TALK?'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      {!isVisualComingSoon && (
        <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-slate-900 border-t border-brand-border dark:border-slate-800 p-4">
          <div className="max-w-6xl mx-auto flex flex-col-reverse sm:flex-row items-center justify-between gap-3">
              <button
                onClick={onBack}
                className="w-full sm:w-auto px-6 py-3 text-brand-text-light dark:text-slate-400 font-medium hover:text-brand-text dark:hover:text-white transition-colors"
              >
                {isFr ? 'Retour' : 'Back'}
              </button>
              {selectedLayoutId ? (
                <button
                  onClick={handleContinue}
                  className="w-full sm:w-auto px-8 py-3 bg-brand-accent text-white font-bold rounded-xl hover:bg-[#c96a3f] transition-all shadow-lg shadow-brand-accent/30 flex items-center justify-center gap-2"
                >
                  {isFr ? 'Continuer' : 'Continue'}
                  <span className="material-symbols-outlined">arrow_forward</span>
                </button>
              ) : (
                <p className="text-sm text-brand-text-muted">
                  {isFr ? 'Sélectionnez un modèle' : 'Select a layout to continue'}
                </p>
              )}
          </div>
        </div>
      )}
      {/* D19 — no-911 layout confirm */}
      {pendingNo911 && (
        <LayoutNo911Dialog
          layout={pendingNo911.layout}
          onChooseAnother={() => setPendingNo911(null)}
          onContinue={confirmNo911}
        />
      )}
      {/* TALK Info Modal */}
      {talkModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setTalkModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-brand-primary" style={{ fontSize: '24px' }}>mic</span>
                <h3 className="text-lg font-bold text-gray-900">
                  {isFr ? 'Qu\u2019est-ce que PARLER?' : 'What is TALK?'}
                </h3>
              </div>
              <button
                onClick={() => setTalkModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 bg-transparent border-none cursor-pointer text-2xl leading-none p-1"
              >
                &times;
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                {isFr
                  ? 'Quand votre proche appuie sur PARLER, AidFone lui demande : \u00ab Qui voulez-vous appeler ? \u00bb Il dit simplement un pr\u00e9nom ou un surnom \u2014 \u00ab Marie \u00bb, \u00ab mon fils \u00bb, \u00ab la pharmacie \u00bb \u2014 et AidFone confirme et compose le num\u00e9ro. Tous les noms et num\u00e9ros viennent de la liste de contacts que vous configurez ici.'
                  : 'When your loved one presses TALK, AidFone asks: \u201cWho do you want to call?\u201d They simply say a name or nickname \u2014 \u201cMarie\u201d, \u201cmy son\u201d, \u201cthe pharmacy\u201d \u2014 and AidFone confirms and dials. All names and numbers come from the contact list you set up here.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Layout Card Component
interface LayoutCardProps {
  layout: LayoutConfig;
  isSelected: boolean;
  isRecommended: boolean;
  onSelect: () => void;
}

const LayoutCard: FC<LayoutCardProps> = ({ layout, isSelected, isRecommended, onSelect }) => {
  const { i18n } = useTranslation();
  const isFr = i18n.language?.startsWith('fr');
  return (
    <div
      onClick={onSelect}
      className={`relative cursor-pointer rounded-3xl overflow-hidden transition-all duration-300 bg-white dark:bg-slate-800/50 ${
        isSelected
          ? 'ring-4 ring-brand-primary shadow-2xl shadow-brand-primary/20 scale-[1.02]'
          : 'ring-1 ring-brand-border dark:ring-slate-700 hover:ring-brand-primary/50 hover:shadow-xl'
      }`}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-brand-primary text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1.5">
          <span className="material-symbols-outlined text-sm">auto_awesome</span>
          {isFr ? 'Recommandé' : 'Recommended'}
        </div>
      )}

      {/* Selected Check */}
      {isSelected && (
        <div className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center shadow-lg">
          <span className="material-symbols-outlined text-white text-lg">check</span>
        </div>
      )}

      {/* Phone Mockup */}
      <div className="p-4 sm:p-6 pb-3 sm:pb-4 bg-[#faf8f5]">
        <div className="relative mx-auto" style={{ maxWidth: '200px' }}>
          <img
            src={getLayoutImage(layout, i18n.language)}
            alt={layout.name}
            className="w-full h-auto"
          />
        </div>
      </div>

      {/* Layout Info */}
      <div className="px-6 pb-6">
        <h3 className="text-base sm:text-lg font-display font-bold text-brand-text dark:text-white mb-1">
          {layout.name}
        </h3>

        {/* Short differentiator (Good-Better-Best) */}
        <p className="text-sm font-medium text-brand-primary mb-2">
          {layout.shortDescription}
        </p>

        <p className="text-sm text-brand-text-light dark:text-slate-400 mb-4">
          {layout.description}
        </p>

        {/* Feature Tags */}
        <div className="flex flex-wrap gap-2">
          <span className="px-2 py-1 bg-brand-background dark:bg-slate-700 rounded-lg text-xs font-medium text-brand-text-light dark:text-slate-300">
            {layout.contactCount} {isFr ? 'contacts' : 'contacts'}
          </span>
          {layout.has911 && (
            <span className="px-2 py-1 bg-red-50 dark:bg-red-900/30 rounded-lg text-xs font-medium text-red-600 dark:text-red-400">
              {isFr ? 'Urgence 911' : '911 Emergency'}
            </span>
          )}
          {layout.hasAssistance && (
            <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 rounded-lg text-xs font-medium text-blue-600 dark:text-blue-400">
              {isFr ? 'Assistant vocal' : 'Voice Assistant'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default LayoutSelection;
